import React from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Skeleton, Tab, Tabs } from '@mui/material'
import { useSnackbar } from 'notistack'
import ErrorMessage from '../../component/shared/component/ErrorMessage'
import Layout from './Layout'
import Form, { toFormValues } from './Form'
import Entries from './Entries'
import { extractErrorMessage } from '../../utils/extractErrorMessage'
import { useGetLuckyDraw, usePatchLuckyDraw } from '../../api/lucky-draw'

export const LuckyDrawEditPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  // The tab lives in the URL so it survives a refresh and can be linked to. Anything unrecognised
  // falls back to the panel rather than rendering nothing.
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'entries' ? 'entries' : 'panel'

  const handleTabChange = (next) => {
    const params = new URLSearchParams(searchParams)

    if (next === 'panel') params.delete('tab')
    else params.set('tab', next)

    setSearchParams(params)
  }

  const { data: draw, isLoading, error, refetch } = useGetLuckyDraw(id)
  const { mutateAsync: update, isLoading: isUpdating } = usePatchLuckyDraw()

  const onSubmit = async (payload) => {
    try {
      // The server reads the id from the body, not the path.
      await update({ id, ...payload })
      enqueueSnackbar('Successfully updated', { variant: 'success' })
      navigate('/lucky-draw')
    } catch (e) {
      enqueueSnackbar(extractErrorMessage(e), { variant: 'error' })
    }
  }

  if (error) {
    return (
      <Layout title='Edit Lucky Draw'>
        <ErrorMessage error={error} onRetry={refetch} />
      </Layout>
    )
  }

  return (
    <Layout title='Edit Lucky Draw'>
      <Tabs value={tab} onChange={(event, next) => handleTabChange(next)}>
        <Tab label='Panel' value='panel' />
        <Tab label='Entries' value='entries' />
      </Tabs>

      {/* Both tabs read the draw — entries for its ticket columns, the panel for its starting
          values — so neither renders before it lands. Mounting the form on loaded values is what
          keeps a background refetch from wiping whatever the user has typed. */}
      {isLoading || !draw ? (
        <Skeleton height={320} />
      ) : tab === 'entries' ? (
        <Entries id={id} source={draw.source} hasWinners={draw.winnerCount > 0} />
      ) : (
        <Form
          key={draw.id}
          defaultValues={toFormValues(draw)}
          onSubmit={onSubmit}
          isSaving={isUpdating}
          submitLabel='SAVE'
          // The source defines the pool every recorded winner came from, so it locks after the
          // first roll.
          hasWinners={draw.winnerCount > 0}
        />
      )}
    </Layout>
  )
}
